import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, Launch as LaunchIcon } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { portfolioProjects, type PortfolioProject } from '../data/portfolio';
import { Reveal, SpotCard, Glow, GridTexture, BRAND_GRAD } from '../components/premium';

const PortfolioPage: React.FC = () => {
  const { language } = useLanguage();
  const [selectedProject, setSelectedProject] = useState<PortfolioProject | null>(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setImageDialogOpen(true);
  };

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', pt: { xs: 13, md: 15 }, pb: { xs: 8, md: 12 }, background: 'linear-gradient(180deg,#F6FAFF,#FFFFFF 40%)' }}>
      <Glow color="#6DABE4" top="-6%" right="-6%" size={520} opacity={0.14} />
      <GridTexture opacity={0.045} />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Reveal>
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
          <Box sx={{ width: 44, height: 3, borderRadius: 3, background: BRAND_GRAD, mx: 'auto', mb: 2.5 }} />
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 600,
              color: '#1A2332',
              mb: 2,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              letterSpacing: '-0.01em',
              textWrap: 'balance',
            }}
          >
            {language === 'th' ? 'ผลงานของเรา' : 'Our Portfolio'}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#666',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            {language === 'th' 
              ? 'โครงการที่เราได้ดำเนินการให้กับลูกค้าด้วยเทคโนโลยีล้ำสมัย'
              : 'Projects we have delivered for our clients using cutting-edge technology'
            }
          </Typography>
        </Box>

        {/* Portfolio Grid */}
        <Grid container spacing={4}>
          {portfolioProjects.map((project) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={project.id}>
              <SpotCard
                onClick={() => setSelectedProject(project)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '18px',
                  border: '1px solid #E6EEF9',
                  boxShadow: '0 14px 36px -20px rgba(0,85,184,0.16)',
                }}
              >
                {project.coverFit === 'contain' ? (
                  <Box sx={{ position: 'relative', height: 250, background: project.coverBg || '#0a1024', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.6 }}>
                    {/* glow + dot texture */}
                    <Box aria-hidden sx={{ position: 'absolute', width: 320, height: 320, top: '-42%', left: '50%', transform: 'translateX(-50%)', background: 'radial-gradient(circle, rgba(99,102,241,0.5) 0%, transparent 60%)', filter: 'blur(26px)' }} />
                    <Box aria-hidden sx={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px)', backgroundSize: '20px 20px', maskImage: 'radial-gradient(ellipse at 50% 42%, #000 25%, transparent 78%)', WebkitMaskImage: 'radial-gradient(ellipse at 50% 42%, #000 25%, transparent 78%)' }} />
                    {project.images.length >= 2 ? (
                      <>
                        {/* mascot avatar (kept small to stay crisp) */}
                        <Box sx={{ position: 'relative', zIndex: 1, width: 104, height: 104, borderRadius: '50%', overflow: 'hidden', border: '3px solid rgba(255,255,255,0.9)', boxShadow: '0 14px 32px -10px rgba(0,0,0,0.6)', background: '#eef2fb' }}>
                          <Box component="img" src={project.images[0]} alt={project.title[language]} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                        {/* brand logo chip (high-res, crisp) */}
                        <Box sx={{ position: 'relative', zIndex: 1, px: 1.8, py: 0.9, borderRadius: '10px', background: 'rgba(255,255,255,0.96)', display: 'flex', alignItems: 'center', boxShadow: '0 8px 20px -10px rgba(0,0,0,0.5)' }}>
                          <Box component="img" src={project.images[1]} alt={`${project.client} logo`} sx={{ maxHeight: 22, maxWidth: 160, objectFit: 'contain', display: 'block' }} />
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ position: 'relative', zIndex: 1, px: 3.5, py: 2.5, borderRadius: '16px', background: 'rgba(255,255,255,0.96)', boxShadow: '0 16px 36px -14px rgba(0,0,0,0.5)' }}>
                        <Box component="img" src={project.images[0]} alt={project.title[language]} sx={{ maxHeight: 78, maxWidth: 200, objectFit: 'contain', display: 'block' }} />
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ height: 250, overflow: 'hidden', backgroundColor: '#f5f5f5' }}>
                    <Box component="img" src={project.images[0]} alt={project.title[language]} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </Box>
                )}
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={project.category}
                      size="small"
                      sx={{
                        backgroundColor: '#0055B8',
                        color: 'white',
                        fontWeight: 500
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#1A2332',
                      mb: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {project.title[language]}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#666', mb: 2 }}
                  >
                    {project.client} • {project.date}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#888',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {project.description[language]}
                  </Typography>
                </CardContent>
              </SpotCard>
            </Grid>
          ))}
        </Grid>
        </Reveal>

        {/* Project Detail Modal */}
        <Dialog
          open={!!selectedProject}
          onClose={() => setSelectedProject(null)}
          maxWidth="xl"
          fullWidth
          scroll="paper"
          PaperProps={{
            sx: {
              width: '80vw',
              height: 'auto',
              maxWidth: '1200px',
              maxHeight: '80vh',
              m: 0,
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
          {selectedProject && (
            <>
              <DialogTitle sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {selectedProject.title[language]}
                </Typography>
                <IconButton
                  onClick={() => setSelectedProject(null)}
                  aria-label={language === 'th' ? 'ปิด' : 'Close'}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 4, flex: 1, overflow: 'auto' }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
                    <strong>{language === 'th' ? 'ลูกค้า:' : 'Client:'}</strong> {selectedProject.client} • {selectedProject.date}
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                    {selectedProject.description[language]}
                  </Typography>
                  
                  {/* Technologies */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {language === 'th' ? 'เทคโนโลยีที่ใช้:' : 'Technologies Used:'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedProject.technologies.map((tech) => (
                        <Chip
                          key={tech}
                          label={tech}
                          variant="outlined"
                          size="small"
                          sx={{ borderColor: '#0055B8', color: '#0055B8' }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Image Gallery */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {language === 'th' ? 'ภาพประกอบ:' : 'Project Gallery:'}
                    </Typography>
                    <Grid container spacing={2}>
                      {selectedProject.images.map((image, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                          <Box
                            sx={{ 
                              overflow: 'hidden',
                              borderRadius: '12px',
                              cursor: 'pointer',
                              transition: 'transform 0.3s ease',
                              '&:hover': {
                                transform: 'scale(1.02)'
                              }
                            }}
                            onClick={() => handleImageClick(image)}
                          >
                            <img
                              src={image}
                              alt={`${selectedProject.title[language]} ${index + 1}`}
                              loading="lazy"
                              style={{
                                height: '200px',
                                objectFit: 'contain',
                                backgroundColor: '#f5f5f5',
                                width: '100%',
                                display: 'block'
                              }}
                            />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  {/* Links */}
                  {(selectedProject.videoUrl || selectedProject.liveUrl) && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {selectedProject.videoUrl && (
                        <Box
                          component="a"
                          href={selectedProject.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: '#0055B8',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          <LaunchIcon fontSize="small" />
                          {language === 'th' ? 'ดูวิดีโอ' : 'Watch Video'}
                        </Box>
                      )}
                      {selectedProject.liveUrl && (
                        <Box
                          component="a"
                          href={typeof selectedProject.liveUrl === 'string' 
                            ? selectedProject.liveUrl 
                            : selectedProject.liveUrl[language]
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: '#0055B8',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          <LaunchIcon fontSize="small" />
                          {language === 'th' ? 'เยี่ยมเว็บไซต์' : 'Visit Website'}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </DialogContent>
            </>
          )}
        </Dialog>

        {/* Image Lightbox */}
        <Dialog
          open={imageDialogOpen}
          onClose={() => setImageDialogOpen(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: {
              width: { xs: '95vw', md: '80vw' },
              height: { xs: '80vh', md: '80vh' },
              maxWidth: '1500px',
              maxHeight: '1000px',
              m: 0,
              backgroundColor: 'rgba(0,0,0,0.9)'
            }
          }}
        >
          <DialogContent sx={{ p: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconButton
              onClick={() => setImageDialogOpen(false)}
              aria-label={language === 'th' ? 'ปิดรูปภาพ' : 'Close image'}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 1,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
              }}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={selectedImage}
              alt="Enlarged view"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default PortfolioPage;